import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box,
    Loader,
    Alert,
    Text,
    Table,
    Paper,
    Group,
    ActionIcon,
    Tooltip
} from '@mantine/core';
import { IconCopy, IconArrowBackUp } from '@tabler/icons-react';
import { entityService } from '../../services/entityServiceSingleton';
import { formatDateTime } from '../../utils/dateTimeUtils';
import { EntityDetailDialog } from '../dialogs';
import { copyToClipboard } from '../../utils/clipboardUtils';
import { usePerEntityTableEntitlement } from '../../hooks/usePerEntityTableEntitlement';
import { filterRequestedAttributesByEntitlements, DEFAULT_TABLE_ATTRIBUTES_BY_ENTITY_TYPE } from '../../utils/tableAttributesUtils';
import { buildJournalEntriesFilter, deduplicateJournalEntriesByTargetId } from '../../utils/journalEntryUtils';
import { LoadingState } from './LoadingState';

function valuesEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    try {
        return JSON.stringify(a) === JSON.stringify(b);
    } catch {
        return false;
    }
}

const JOURNAL_ENTRY_COLUMNS = [
    { attrId: 'Id', label: 'Id', key: 'id' },
    { attrId: 'DisplayName', label: 'Display Name', key: 'displayName' },
    { attrId: 'TargetType', label: 'Target Type', key: 'targetType' },
    { attrId: 'Attribute', label: 'Attribute', key: 'attribute' },
    { attrId: 'TargetId', label: 'Target Id', key: 'targetId' },
    { attrId: 'Recorded', label: 'Recorded', key: 'recorded' },
    { attrId: 'Source', label: 'Source', key: 'source' },
    { attrId: 'Status', label: 'Status', key: 'status' }
] as const;

export interface JournalEntriesViewProps {
    entityType?: string;
    targetId?: string;
    /** When set, filter journal entries to this actor (user id). */
    actorId?: string;
    /** When true, show one row per TargetId (latest Recorded). Accumulates across loaded journal pages. */
    deduplicateByTargetId?: boolean;
    itemsPerPage?: number;
    /** When set (e.g. Journal tab of entity detail), Revert icon is shown only when current value equals journal updated value */
    currentEntity?: Record<string, unknown> | null;
    /** When false, tier2 entitlement is not fetched (default true so columns/API are filtered by entitlement when not admin). */
    authResolved?: boolean;
}

export const JournalEntriesView: React.FC<JournalEntriesViewProps> = ({
    entityType,
    targetId,
    actorId,
    deduplicateByTargetId = false,
    itemsPerPage = 25,
    currentEntity = null,
    authResolved = true
}) => {
    const [journalEntries, setJournalEntries] = useState<any[]>([]);
    const [rawJournalEntryCount, setRawJournalEntryCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [entitlementResolvedKey, setEntitlementResolvedKey] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [hasPreviousPage, setHasPreviousPage] = useState(false);
    const [selectedJournalEntry, setSelectedJournalEntry] = useState<any | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [revertingId, setRevertingId] = useState<string | null>(null);
    const [revertError, setRevertError] = useState<string | null>(null);
    // We intentionally do not request Operation in list queries (it can be huge). Revert fetches Operation on demand.

    const { allowedTableAttributeIds, tier2Error } = usePerEntityTableEntitlement({
        entityType: 'journalentry',
        authResolved,
        onlyWhenEntityTypeSet: false,
        onResolved: () => setEntitlementResolvedKey((k) => k + 1)
    });

    const visibleColumns = useMemo(() => {
        // In this view we always filter by TargetType (entityType prop), so showing TargetType is redundant.
        // When targetId is provided, TargetId is also constant.
        const hidden = new Set<string>(['TargetType']);
        if (targetId) hidden.add('TargetId');

        // Start from defaults; if entitlements are available, filter down further.
        const base = JOURNAL_ENTRY_COLUMNS.filter((c) => !hidden.has(c.attrId));

        if (!Array.isArray(allowedTableAttributeIds)) return base;

        const allowedLower = new Set(allowedTableAttributeIds.map((a) => (a || '').toLowerCase()));
        const alwaysShow = new Set(['id', 'displayname']);
        return base.filter(
            (c) => alwaysShow.has(c.attrId.toLowerCase()) || allowedLower.has(c.attrId.toLowerCase())
        );
    }, [allowedTableAttributeIds, targetId]);

    const handleJournalEntryClick = useCallback((entry: any) => {
        setSelectedJournalEntry(entry);
        setDetailDialogOpen(true);
    }, []);

    const handleCloseDetailDialog = useCallback(() => {
        setDetailDialogOpen(false);
        setSelectedJournalEntry(null);
    }, []);

    useEffect(() => {
        setCurrentPage(1);
        setRawJournalEntryCount(0);
    }, [entityType, targetId, actorId, deduplicateByTargetId]);

    const loadJournalEntries = useCallback(async (page: number = 1) => {
        if (!entityType) {
            setJournalEntries([]);
            setRawJournalEntryCount(0);
            setLoading(false);
            return;
        }

        if (deduplicateByTargetId && !actorId?.trim()) {
            setJournalEntries([]);
            setRawJournalEntryCount(0);
            setLoading(false);
            return;
        }

        // Do not call the API until tier2 entitlement has resolved (avoid requesting all attributes then refetching)
        if (allowedTableAttributeIds === null) {
            setLoading(true);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const filter = buildJournalEntriesFilter({
                entityType,
                targetId,
                actorId: actorId?.trim() || undefined,
            });

            // Requested attributes filtered by tier2 entitlements (do not call API when allowedTableAttributeIds is null).
            const defaultAttrs = DEFAULT_TABLE_ATTRIBUTES_BY_ENTITY_TYPE.journalentry ?? ['Id', 'DisplayName', 'TargetType', 'Attribute', 'TargetId', 'Recorded', 'Source', 'Status'];
            // We always filter by TargetType here, so don't request it just to display it.
            const requestedAttributes = targetId
                ? defaultAttrs.filter((a) => a !== 'TargetType' && a !== 'TargetId')
                : defaultAttrs.filter((a) => a !== 'TargetType');
            const journalEntryAttributes = filterRequestedAttributesByEntitlements(requestedAttributes, allowedTableAttributeIds, { isJournalEntry: true });

            const response = await entityService.getEntitiesByType(
                'JournalEntry',
                page,
                itemsPerPage,
                undefined, // Journal entries should not send sort parameter
                filter,
                journalEntryAttributes
            );

            if (response.data && Array.isArray(response.data)) {
                const batch = response.data;
                if (deduplicateByTargetId) {
                    // Dedupe within the current journal page only so pagination shows a new slice each time.
                    // Accumulating across pages kept the newest Recorded per TargetId from page 1, so later pages looked unchanged.
                    setRawJournalEntryCount(batch.length);
                    setJournalEntries(deduplicateJournalEntriesByTargetId(batch) as any[]);
                } else {
                    setRawJournalEntryCount(batch.length);
                    setJournalEntries(batch);
                }
                // For journal entries, use HasMore instead of totalResults (which is always 0)
                // If hasMore is undefined (not present) or true, we can go to next page
                const hasMoreValue = response.hasMore;
                setHasMore(hasMoreValue === undefined || hasMoreValue === true);
                setHasPreviousPage(response.hasPreviousPage ?? (page > 1));
            } else {
                setJournalEntries([]);
                setRawJournalEntryCount(0);
                setHasMore(false);
                setHasPreviousPage(false);
            }
        } catch (err: any) {
            console.error('Error loading journal entries:', err);
            setError(err.message || 'Failed to load journal entries');
            setJournalEntries([]);
            setRawJournalEntryCount(0);
        } finally {
            setLoading(false);
        }
    }, [entityType, targetId, actorId, deduplicateByTargetId, itemsPerPage, entityService, allowedTableAttributeIds]);

    useEffect(() => {
        loadJournalEntries(currentPage);
    }, [loadJournalEntries, currentPage, entitlementResolvedKey]);

    const handleRevert = useCallback(async (entry: any, e: React.MouseEvent) => {
        e.stopPropagation();
        const entryId = entry.Id || entry.id;
        const targetId = entry.TargetId || entry.targetId;
        const targetType = entry.TargetType || entry.targetType;
        if (!entryId || !targetId || !targetType) {
            setRevertError('Missing journal entry Id, TargetId, or TargetType.');
            return;
        }
        setRevertingId(entryId);
        setRevertError(null);
        try {
            const full = await entityService.getEntityById(entryId, 'JournalEntry', false);
            const opRecord = full?.Operation || full?.operation;
            if (!opRecord) {
                setRevertError('Cannot revert: operation data not available for this journal entry.');
                return;
            }
            const op = (opRecord.Operation ?? opRecord.operation ?? '').toString();
            const path = (opRecord.Attribute ?? opRecord.attribute ?? '').toString();
            const value = opRecord.OperationValue ?? opRecord.operationValue ?? opRecord.value;
            const previousValue = opRecord.PreviousValue ?? opRecord.previousValue ?? opRecord.previous;
            const updatedValue = opRecord.UpdatedValue ?? opRecord.updatedValue ?? opRecord.OperationValue ?? opRecord.operationValue ?? opRecord.value;
            if (!path) {
                setRevertError('Cannot revert: attribute path not available.');
                return;
            }

            // Safety check: only allow revert from the journal tab when current value still matches what the journal entry set.
            if (currentEntity) {
                const key = Object.keys(currentEntity).find((k) => k.toLowerCase() === path.toLowerCase());
                const currentVal = key != null ? (currentEntity as any)[key] : undefined;
                if (!valuesEqual(currentVal, updatedValue)) {
                    setRevertError('Cannot revert: the current entity value no longer matches this journal entry.');
                    return;
                }
            }

            let inverseOp: { op: string; path: string; value?: unknown };
            if (op === 'Add') {
                inverseOp = { op: 'Remove', path, value };
            } else if (op === 'Remove') {
                inverseOp = { op: 'Add', path, value };
            } else if (op === 'Replace' || op === 'Delete') {
                if (previousValue === undefined) {
                    setRevertError('Cannot revert: previous value not available.');
                    return;
                }
                inverseOp = { op: 'Replace', path, value: previousValue };
            } else {
                setRevertError(`Cannot revert: unsupported operation type "${op}".`);
                return;
            }
            await entityService.patchEntity(targetId, { Operations: [inverseOp] }, targetType);
            setRevertError(null);
            await loadJournalEntries(currentPage);
        } catch (err: any) {
            setRevertError(err?.response?.data?.Message ?? err?.message ?? 'Revert failed.');
        } finally {
            setRevertingId(null);
        }
    }, [entityService, currentPage, loadJournalEntries, currentEntity]);

    const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
        setCurrentPage(newPage);
    };

    // Generate page numbers going backwards from current page
    // If on page 5, show pages 5, 4, 3, 2, 1
    const visiblePages = useMemo(() => {
        const pages: number[] = [];
        for (let i = currentPage; i >= 1; i--) {
            pages.push(i);
        }
        return pages;
    }, [currentPage]);

    // Handle ID copy
    const handleCopyId = useCallback(async (id: string, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent row click
        await copyToClipboard(id);
    }, []);

    // Shorten ID for display (first 8 characters + ellipsis)
    const shortenId = useCallback((id: string | undefined): string => {
        if (!id) return 'N/A';
        if (id.length <= 12) return id;
        return `${id.substring(0, 8)}...`;
    }, []);

    // Show loader while fetching data or while tier2 entitlement is still resolving (so we never call API before checking entitlements)
    const entitlementLoading = entityType != null && allowedTableAttributeIds === null;
    if ((loading && journalEntries.length === 0) || entitlementLoading) {
        return (
            <LoadingState variant="centered" minHeight={200} />
        );
    }

    if (error) {
        return (
            <Alert color="red" mb="md">
                {error}
            </Alert>
        );
    }

    if (!entityType) {
        return (
            <Box p="md">
                <Text size="sm" c="dimmed">
                    Select an entity type to view journal entries.
                </Text>
            </Box>
        );
    }

    if (journalEntries.length === 0) {
        return (
            <Box p="md">
                <Text size="sm" c="dimmed">
                    {deduplicateByTargetId && actorId
                        ? `No ${entityType} entries found for this user${rawJournalEntryCount > 0 ? ` (${rawJournalEntryCount} journal rows loaded)` : ''}.`
                        : `No journal entries found${targetId ? ` for this ${entityType}` : ` for ${entityType} entities`}${actorId ? ' for this actor' : ''}.`}
                </Text>
            </Box>
        );
    }

    return (
        <Box>
            {deduplicateByTargetId && rawJournalEntryCount > journalEntries.length && (
                <Text size="sm" c="dimmed" mb="sm">
                    {journalEntries.length} {entityType} {journalEntries.length === 1 ? 'item' : 'items'} on this page (from {rawJournalEntryCount} journal {rawJournalEntryCount === 1 ? 'entry' : 'entries'})
                </Text>
            )}
            {tier2Error && (
                <Alert color="red" mb="md" title="Entitlements error">
                    {tier2Error}
                </Alert>
            )}
            {revertError && (
                <Alert
                    color="red"
                    mb="md"
                    withCloseButton
                    onClose={() => setRevertError(null)}
                >
                    {revertError}
                </Alert>
            )}
            <Paper withBorder shadow="xs">
                <Table.ScrollContainer minWidth={500}>
                    <Table highlightOnHover withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                {visibleColumns.map((col) => (
                                    <Table.Th key={col.attrId}><Text fw={600}>{col.label}</Text></Table.Th>
                                ))}
                                {currentEntity != null && (
                                    <Table.Th w={72} ta="center"><Text fw={600}>Revert</Text></Table.Th>
                                )}
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {journalEntries.map((entry, index) => {
                                const id = entry.Id || entry.id || '';
                                const isRevertingThis = revertingId === id;
                                const showRevert = currentEntity != null;

                                return (
                                    <Table.Tr
                                        key={id || `journal-row-${index}`}
                                        onClick={() => handleJournalEntryClick(entry)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {visibleColumns.map((col) => {
                                            const raw = entry[col.attrId] ?? entry[col.key];
                                            if (col.attrId === 'Id') {
                                                return (
                                                    <Table.Td key={col.attrId}>
                                                        <Group gap={4} wrap="nowrap">
                                                            <Text size="sm" ff="monospace" title={raw || 'N/A'}>
                                                                {shortenId(raw)}
                                                            </Text>
                                                            {raw && (
                                                                <Tooltip label="Copy ID to clipboard">
                                                                    <ActionIcon size="sm" variant="subtle" onClick={(e) => handleCopyId(raw, e)} aria-label="Copy ID">
                                                                        <IconCopy size={14} />
                                                                    </ActionIcon>
                                                                </Tooltip>
                                                            )}
                                                        </Group>
                                                    </Table.Td>
                                                );
                                            }
                                            if (col.attrId === 'Recorded') {
                                                const formatted = raw && typeof raw === 'string' ? formatDateTime(raw) : 'N/A';
                                                return (
                                                    <Table.Td key={col.attrId} title={raw && typeof raw === 'string' ? raw : undefined}>
                                                        {formatted}
                                                    </Table.Td>
                                                );
                                            }
                                            if (col.attrId === 'DisplayName') {
                                                const text = raw != null && raw !== '' ? String(raw) : 'N/A';
                                                return (
                                                    <Table.Td key={col.attrId} title={text !== 'N/A' ? text : undefined}>
                                                        <Text size="sm" maw={360} truncate="end">
                                                            {text}
                                                        </Text>
                                                    </Table.Td>
                                                );
                                            }
                                            return <Table.Td key={col.attrId}>{raw ?? 'N/A'}</Table.Td>;
                                        })}
                                        {currentEntity != null && (
                                            <Table.Td ta="center" onClick={(e) => e.stopPropagation()}>
                                                {showRevert ? (
                                                    <Tooltip label="Revert this change (send inverse operation to target entity)">
                                                        <ActionIcon
                                                            size="sm"
                                                            variant="subtle"
                                                            color="gray"
                                                            disabled={isRevertingThis}
                                                            onClick={(e) => handleRevert(entry, e)}
                                                            aria-label="Revert"
                                                        >
                                                            {isRevertingThis ? <Loader size={14} /> : <IconArrowBackUp size={14} />}
                                                        </ActionIcon>
                                                    </Tooltip>
                                                ) : null}
                                            </Table.Td>
                                        )}
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>

            {(hasMore || hasPreviousPage || currentPage > 1) && (
                <Group mt="md" justify="center" align="center">
                    <Group gap="xs" align="center" wrap="wrap">
                        <Text size="sm" c="dimmed" mr="xs">
                            Page {currentPage}
                        </Text>
                        <Group gap={4} align="center">
                            {/* Show page numbers going backwards from current page (e.g., 5, 4, 3, 2, 1) */}
                            {visiblePages.slice(0, 5).map((pageNum) => (
                                <Box
                                    key={pageNum}
                                    component="button"
                                    type="button"
                                    onClick={() => handlePageChange({} as React.ChangeEvent<unknown>, pageNum)}
                                    style={{
                                        minWidth: 32,
                                        height: 32,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        borderRadius: 4,
                                        backgroundColor: pageNum === currentPage ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-default-hover)',
                                        color: pageNum === currentPage ? 'var(--mantine-color-white)' : 'var(--mantine-color-text)',
                                        border: 'none',
                                        fontWeight: pageNum === currentPage ? 'bold' : 'normal',
                                    }}
                                >
                                    {pageNum}
                                </Box>
                            ))}
                        </Group>
                        {/* Previous button */}
                        <Box
                            component="button"
                            type="button"
                            onClick={() => hasPreviousPage && handlePageChange({} as React.ChangeEvent<unknown>, currentPage - 1)}
                            style={{
                                minWidth: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: hasPreviousPage ? 'pointer' : 'not-allowed',
                                borderRadius: 4,
                                backgroundColor: 'var(--mantine-color-default-hover)',
                                opacity: hasPreviousPage ? 1 : 0.5,
                                border: 'none',
                            }}
                        >
                            ←
                        </Box>
                        {/* Next button */}
                        <Box
                            component="button"
                            type="button"
                            onClick={() => hasMore && handlePageChange({} as React.ChangeEvent<unknown>, currentPage + 1)}
                            style={{
                                minWidth: 32,
                                height: 32,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: hasMore ? 'pointer' : 'not-allowed',
                                borderRadius: 4,
                                backgroundColor: 'var(--mantine-color-default-hover)',
                                opacity: hasMore ? 1 : 0.5,
                                border: 'none',
                            }}
                        >
                            →
                        </Box>
                        {hasMore && (
                            <Text size="sm" c="dimmed" ml="xs">
                                (more)
                            </Text>
                        )}
                    </Group>
                </Group>
            )}

            {/* Journal Entry Detail Dialog */}
            {selectedJournalEntry && (
                <EntityDetailDialog
                    open={detailDialogOpen}
                    entity={selectedJournalEntry}
                    entityType="JournalEntry"
                    onClose={handleCloseDetailDialog}
                />
            )}
        </Box>
    );
};
